/**
 * Regras de leitura da carteira (redesign de Clientes, 29/10/2026): faixa de
 * saúde, prazo relativo, risco, etapa e próxima ação sugerida. Funções puras.
 */
import { CLIENTE_STATUS_FLOW } from "../../../../utils/clientes";
import { startOfDay } from "../../../../utils/status";

const MS_POR_DIA = 1000 * 60 * 60 * 24;

export const FAIXAS = Object.freeze({ SAUDAVEL: "ok", ATENCAO: "warn", CRITICO: "bad" });
export const ROTULO_DA_FAIXA = Object.freeze({ ok: "Saudável", warn: "Atenção", bad: "Crítico" });
const SAUDE_MINIMA_SAUDAVEL = 95;
const SAUDE_MINIMA_ATENCAO = 80;
const ATRASO_MAXIMO_ATENCAO = 30;

/** Janela de "entrega em breve" (dias). */
export const DIAS_DE_ENTREGA_PROXIMA = 7;

/** Quem espera quem: os valores de `AnaliseCliente.Espera` da Central. */
export const ESPERA = Object.freeze({
  EQUIPE: "equipe",
  CLIENTE: "cliente",
  NINGUEM: "ninguem",
  INDEFINIDO: "indefinido",
});
export const ROTULO_DA_ESPERA = Object.freeze({
  equipe: "Equipe",
  cliente: "Cliente",
  ninguem: "Ninguém",
  indefinido: "Indefinido",
});
/** Quem espera "manda" quando o cliente tem vários pedidos. */
const PRIORIDADE_DA_ESPERA = [ESPERA.EQUIPE, ESPERA.CLIENTE, ESPERA.NINGUEM, ESPERA.INDEFINIDO];

export function esperaMaisUrgente(esperas) {
  return PRIORIDADE_DA_ESPERA.find((e) => esperas.includes(e)) || ESPERA.INDEFINIDO;
}

/** Dias de `hoje` até `data` (negativo = já passou); null sem data. */
export function diasAte(data, hoje = new Date()) {
  const alvo = startOfDay(data);
  const base = startOfDay(hoje);
  if (!alvo || !base) return null;
  return Math.round((alvo.getTime() - base.getTime()) / MS_POR_DIA);
}

function dias(n) {
  return n === 1 ? "1 dia" : `${n} dias`;
}

/** "atrasada 3 dias", "hoje", "amanhã", "em 12 dias". */
export function prazoRelativo(diasAteAData) {
  if (diasAteAData == null) return "sem data";
  if (diasAteAData < 0) return `atrasada ${dias(-diasAteAData)}`;
  if (diasAteAData === 0) return "hoje";
  if (diasAteAData === 1) return "amanhã";
  return `em ${dias(diasAteAData)}`;
}

/** Tom do prazo: `late`, `today`, `soon` (até 7 dias) ou vazio. */
export function tomDoPrazo(diasAteAData, { atrasoAcumulado = 0 } = {}) {
  if (diasAteAData == null) return atrasoAcumulado > 0 ? "late" : "";
  if (diasAteAData < 0 || atrasoAcumulado > 0) return "late";
  if (diasAteAData === 0) return "today";
  if (diasAteAData <= DIAS_DE_ENTREGA_PROXIMA) return "soon";
  return "";
}

export function entregaVencida(diasAteAData) {
  return diasAteAData != null && diasAteAData < 0;
}

export function entregaEmBreve(diasAteAData) {
  return diasAteAData != null && diasAteAData >= 0 && diasAteAData <= DIAS_DE_ENTREGA_PROXIMA;
}

/** Atrasado = entrega vencida ou atraso acumulado no contrato. */
export function estaAtrasado({ diasProximaEntrega, atrasoAcumulado }) {
  return entregaVencida(diasProximaEntrega) || atrasoAcumulado > 0;
}

/**
 * Saudável: saúde ≥ 95% e nada atrasado. Atenção: saúde ≥ 80% e menos de 30
 * dias acumulados. Crítico: o resto.
 */
export function faixaDeSaude({ saude, atrasado, atrasoAcumulado }) {
  if (saude >= SAUDE_MINIMA_SAUDAVEL && !atrasado) return FAIXAS.SAUDAVEL;
  if (saude >= SAUDE_MINIMA_ATENCAO && atrasoAcumulado < ATRASO_MAXIMO_ATENCAO) return FAIXAS.ATENCAO;
  return FAIXAS.CRITICO;
}

const PESO_ENTREGA_VENCIDA = 40;
const PESO_ENTREGA_EM_BREVE = 20;
const PESO_POR_DIA_ACUMULADO = 2;
const PESO_POR_PENDENCIA = 3;
const PESO_ESPERA_EQUIPE = 10;

/** Pontos de risco: ordena a lista (maior primeiro) e escolhe as ações sugeridas. */
export function pontosDeRisco({ saude, atrasoAcumulado, diasProximaEntrega, pendencias = 0, espera }) {
  let pontos = (100 - saude) + PESO_POR_DIA_ACUMULADO * atrasoAcumulado + PESO_POR_PENDENCIA * pendencias;
  if (entregaVencida(diasProximaEntrega)) pontos += PESO_ENTREGA_VENCIDA;
  else if (entregaEmBreve(diasProximaEntrega)) pontos += PESO_ENTREGA_EM_BREVE;
  if (espera === ESPERA.EQUIPE) pontos += PESO_ESPERA_EQUIPE;
  return pontos;
}

const ATRASO_QUE_PEDE_REPLANEJAR = 30;

function plural(n, um, varios) {
  return `${n} ${n === 1 ? um : varios}`;
}

/** Próxima ação sugerida para o cliente, na ordem de prioridade do negócio. */
export function acaoSugerida({ diasProximaEntrega, atrasoAcumulado, pendencias = 0, espera, proximaEntregaLabel }) {
  if (entregaVencida(diasProximaEntrega)) {
    return `entrega vencida há ${dias(-diasProximaEntrega)}: confirmar nova data com o cliente`;
  }
  if (atrasoAcumulado > ATRASO_QUE_PEDE_REPLANEJAR) {
    return `${atrasoAcumulado} dias de atraso acumulado: replanejar os ambientes vencidos`;
  }
  if (espera === ESPERA.EQUIPE && pendencias > 0) {
    return `responder ${plural(pendencias, "pendência aberta", "pendências abertas")}`;
  }
  if (espera === ESPERA.CLIENTE) return "cobrar retorno do cliente";
  return proximaEntregaLabel ? `acompanhar a entrega de ${proximaEntregaLabel}` : "acompanhar os ambientes em andamento";
}

/** Com quem fica a próxima ação sugerida. */
export function donoDaAcao(espera) {
  return espera === ESPERA.CLIENTE ? "Cliente" : "Equipe";
}

// ------------------------------------------------------------
// Etapa do cliente: o status do ambiente em andamento mais atrás no fluxo,
// agrupado em poucas etapas legíveis.
// ------------------------------------------------------------

const ETAPAS = Object.freeze({
  liberacao: { rotulo: "Aguard. liberação", tom: "neu" },
  definicoes: { rotulo: "Aguard. definições", tom: "warn" },
  medicao: { rotulo: "Aguardando medição", tom: "warn" },
  projeto: { rotulo: "Em projeto", tom: "neu" },
  revisao: { rotulo: "Em revisão", tom: "neu" },
  aprovacao: { rotulo: "Em aprovação", tom: "vio" },
  producao: { rotulo: "Em produção", tom: "acc" },
  entrega: { rotulo: "Aguard. entrega", tom: "acc" },
  entregue: { rotulo: "Entregue", tom: "ok" },
});

const ETAPA_DO_STATUS = {
  "Aguardando liberação": "liberacao",
  "Aguardando definições": "definicoes",
  "Aguardando Medição": "medicao",
  "Aguardando Projeto Executivo": "projeto",
  "Aguardando Revisão": "revisao",
  "Aguardando Aprovação": "aprovacao",
  "Aguardando Entrega": "entrega",
  Entregue: "entregue",
};

const INDICE_DO_STATUS = new Map(CLIENTE_STATUS_FLOW.map((status, i) => [status, i]));

export function etapaDoStatus(status) {
  const chave = ETAPA_DO_STATUS[status] || (INDICE_DO_STATUS.has(status) ? "producao" : null);
  return chave ? { chave, ...ETAPAS[chave] } : { chave: "outro", rotulo: status || "—", tom: "neu" };
}

/** Etapa do cliente: o ambiente em andamento mais atrás; todos entregues = "Entregue". */
export function etapaDoCliente(ambientes) {
  const emAndamento = (ambientes || []).filter((a) => a.status !== "Entregue");
  if (!emAndamento.length) return etapaDoStatus("Entregue");
  const maisAtras = emAndamento.reduce((atual, a) =>
    (INDICE_DO_STATUS.get(a.status) ?? Infinity) < (INDICE_DO_STATUS.get(atual.status) ?? Infinity) ? a : atual,
  );
  return etapaDoStatus(maisAtras.status);
}
